import { UsersThree } from '@phosphor-icons/react';
import { Button as AriaButton } from 'react-aria-components';
import { Icon } from '../atoms/Icon';
import { cn } from '../lib/cn';
import { personSolid } from '../lib/person';
import type { WeekDay, WeekPerson } from './WeekStrip';
import { uiText } from '../i18n';

export interface WeekListProps {
  days: WeekDay[];
  /** Tapping an event opens it (details, edit) – `date` is the day it was tapped on. */
  onItemPress?: (id: string, date: string) => void;
  className?: string;
}

/**
 * The coming days, compact: one line per day, events as small chips. Today is not repeated
 * here – it has the big day plan.
 */
export function WeekList({ days, onItemPress, className }: WeekListProps) {
  return (
    <ol className={cn('flex flex-col divide-y divide-border-subtle', className)}>
      {days.map((day) => (
        <li
          key={day.key}
          className="grid grid-cols-[5.5rem_1fr] items-start gap-gap-md py-inset-sm"
        >
          <span className="flex flex-col leading-tight">
            <span className="font-display font-black">{day.label}</span>
            <span className="text-xs text-text-muted tabular-nums">{day.date}</span>
          </span>
          {day.items.length === 0 ? (
            <span className="text-sm text-text-muted">{uiText().free}</span>
          ) : (
            <ul
              className={cn(
                'flex flex-wrap',
                onItemPress ? '-my-inset-xs gap-x-gap-xs' : 'gap-gap-xs',
              )}
            >
              {day.items.map((item) => {
                const family = item.people === 'family';
                const chip = cn(
                  'inline-flex items-center gap-gap-xs rounded-chip px-inset-xs py-1 text-sm',
                  family ? 'bg-accent-secondary text-accent-on-accent' : 'bg-surface-sunken',
                );
                const content = (
                  <>
                    {family ? (
                      <Icon icon={UsersThree} size="sm" weight="fill" />
                    ) : (
                      <span aria-hidden className="flex -space-x-1">
                        {(item.people as WeekPerson[]).map((p) => (
                          <span
                            key={p.name}
                            className={cn(
                              'size-2.5 rounded-avatar ring-2 ring-surface-sunken',
                              personSolid[p.color],
                            )}
                          />
                        ))}
                      </span>
                    )}
                    {item.time && <span className="font-semibold tabular-nums">{item.time}</span>}
                    <span className="font-semibold">{item.title}</span>
                    {!family && (
                      <span className="sr-only">
                        {(item.people as WeekPerson[]).map((p) => p.name).join(', ')}
                      </span>
                    )}
                  </>
                );
                if (!onItemPress) {
                  return (
                    <li key={item.id} className={chip}>
                      {content}
                    </li>
                  );
                }
                return (
                  <li key={item.id} className="flex">
                    {/* the chip stays small; the button around it is a full touch target */}
                    <AriaButton
                      onPress={() => onItemPress(item.id, day.key)}
                      className="group flex min-h-touch-min cursor-pointer items-center rounded-chip outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus"
                    >
                      <span
                        className={cn(
                          chip,
                          'group-data-hovered:ring-2 group-data-hovered:ring-border-strong group-data-pressed:scale-99',
                        )}
                      >
                        {content}
                      </span>
                    </AriaButton>
                  </li>
                );
              })}
            </ul>
          )}
        </li>
      ))}
    </ol>
  );
}
